import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

interface ForgotPasswordFormValues {
  email: string;
}

const PasswordRecoveryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const BrandIcon = () => (
    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
      <img 
        src="/logo.png" 
        alt="Logo" 
        style={{ 
          width: '140px', 
          height: '140px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto'
        }} 
      />
    </div>
  );

  const onFinish = async (values: ForgotPasswordFormValues) => {
    setLoading(true);
    try {
      await authAPI.forgotPassword(values.email);
      setEmailSent(true);
      message.success('Reset link sent to your email');
    } catch (error: any) {
      console.error('Password recovery error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send reset link. Please try again.';
      message.error(errorMessage, 5);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="auth-page">
        <Card className="auth-card" bodyStyle={{ padding: '1.25rem' }}>
          <div className="brand-header" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <BrandIcon />
          </div>
          
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <MailOutlined style={{ fontSize: '3rem', color: '#607afb', marginBottom: '1rem' }} />
            <Title level={3} style={{ color: '#0d0f1c', marginBottom: '1rem' }}>
              Check your email
            </Title>
            <Text style={{ color: '#47569e', display: 'block', marginBottom: '2rem' }}>
              We've sent a password reset link to your email address.
              Please check your inbox and follow the instructions.
            </Text>
            <Button 
              type="link" 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
              style={{ color: '#607afb' }}
            >
              Back to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Card className="auth-card" bodyStyle={{ padding: '1.25rem' }}>
        <div className="brand-header" style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <BrandIcon />
        </div>

        <Title level={2} className="page-title">
          Forgot Password?
        </Title>
        
        <Text style={{ 
          color: '#47569e', 
          display: 'block', 
          textAlign: 'center', 
          marginBottom: '1.25rem',
          fontSize: '14px'
        }}>
          Enter your email address and we'll send you a link to reset your password.
        </Text>

        <Form
          form={form}
          name="forgot-password"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
        >
          <Form.Item
            label={<span className="form-item-label">Email Address</span>}
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input 
              prefix={<MailOutlined style={{ color: '#47569e' }} />}
              placeholder="Enter your email address"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: '1rem' }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="login-btn"
            >
              Send Reset Link
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <Button 
            type="link" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/login')}
            style={{ color: '#607afb' }}
          >
            Back to Login
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PasswordRecoveryPage;
